/**
 * Shared content-page hero used by /glossary, /faq, /pricing,
 * /compare/*, /blog and individual blog posts.
 *
 * Keeps brutalist visual language (black borders, hard shadows,
 * brand color block) but is more compact than the homepage hero.
 */
import type { ReactNode } from "react";

export type ContentHeroBreadcrumb = { label: string; href?: string };

export function ContentHero({
  eyebrow,
  eyebrowColor = "#A3E635",
  titleTop,
  titleBottom,
  bottomColor = "#A3E635",
  lead,
  meta,
  breadcrumbs,
  children,
}: {
  eyebrow: string;
  eyebrowColor?: string;
  titleTop: string;
  titleBottom?: string;
  bottomColor?: string;
  lead: string;
  meta?: string[];
  breadcrumbs?: ContentHeroBreadcrumb[];
  children?: ReactNode;
}) {
  return (
    <section className="border-b-4 border-black bg-black px-4 py-12 sm:px-6 lg:px-8 lg:py-16">
      <div className="mx-auto max-w-5xl">
        {breadcrumbs && breadcrumbs.length > 0 ? (
          <nav className="mb-6 flex flex-wrap items-center gap-2 text-[11px] font-black uppercase tracking-[0.18em] text-zinc-400">
            {breadcrumbs.map((crumb, i) => (
              <span key={`${crumb.label}-${i}`} className="flex items-center gap-2">
                {crumb.href ? (
                  <a href={crumb.href} className="hover:text-white transition-colors">
                    {crumb.label}
                  </a>
                ) : (
                  <span className="text-white/90">{crumb.label}</span>
                )}
                {i < breadcrumbs.length - 1 ? <span className="text-zinc-600">/</span> : null}
              </span>
            ))}
          </nav>
        ) : null}

        <div
          className="inline-flex items-center gap-2 border-4 border-white px-3 py-1.5"
          style={{ backgroundColor: eyebrowColor }}
        >
          <span className="text-[11px] font-black uppercase tracking-[0.22em] text-black">
            {eyebrow}
          </span>
        </div>

        <h1 className="mt-6 text-4xl font-black uppercase leading-none text-white sm:text-5xl lg:text-6xl">
          {titleTop}
          {titleBottom ? (
            <>
              <br />
              <span style={{ color: bottomColor }}>{titleBottom}</span>
            </>
          ) : null}
        </h1>

        <p className="mt-6 max-w-3xl text-base font-bold leading-7 text-zinc-300 sm:text-lg">
          {lead}
        </p>

        {meta && meta.length > 0 ? (
          <div className="mt-6 flex flex-wrap gap-2">
            {meta.map((m) => (
              <span
                key={m}
                className="border-2 border-white/30 bg-white/10 px-3 py-1 text-[11px] font-black uppercase tracking-[0.18em] text-white"
              >
                {m}
              </span>
            ))}
          </div>
        ) : null}

        {children ? <div className="mt-6">{children}</div> : null}
      </div>
    </section>
  );
}
